// item-type-badge.ts — item-type → badge colour, MATCHED to the runic icon system.
//
// A search result's type badge should read as the same colour as the rune medallion / type
// icon rendered beside it. These colours mirror rune-icon-engine.ts: TYPE_COLOR (the exact
// medallion plate colour for every rune-eligible type) plus FAMILY_HUE values for the common
// non-rune types, so the whole set reads as one palette. Text ink is chosen by luminance the
// same way the rune engine picks glyph ink (inkFor): dark on bright fills, white on dark —
// which keeps light plates like passive-ability cyan legible.
//
// Emitted as an inline style so the JS-generated badges stay self-contained (no CSS build).
// Shared by the navbar, general (/search) and add-inventory result renderers.
//
// KEEP IN SYNC with rune-icon-engine.ts TYPE_COLOR / FAMILY_HUE if those identity colours move.

// Identity colour per item_type. Rune-eligible types use the EXACT rune TYPE_COLOR (so the badge
// matches the medallion); non-rune types borrow the nearest rune FAMILY_HUE so they stay on-palette.
const TYPE_COLOR: Record<string, string> = {
    // ── rune-eligible: exact medallion plate colours (rune-icon-engine.ts TYPE_COLOR) ──
    spell: '#818cf8', menta: '#818cf8', curse: '#d946ef', sanguine: '#e0245e',
    ability: '#7aa2ff', full_ability: '#2dd4bf', passive_ability: '#67e8f9',
    attack_ability: '#ff7a7a', quick_ability: '#5fd28a', free_ability: '#8b93a7',
    reaction_ability: '#9333ea', opportunity_attack_ability: '#fbbf24', lair_ability: '#f97316',
    regional_effect: '#f97316', trap_ability: '#a3e635', trap: '#a3e635',

    // ── non-rune: nearest rune FAMILY_HUE so the set reads as one system ──
    weapon: '#fb7185', ammunition: '#fb7185',           // FAMILY_HUE.weapon
    armor: '#7dd3fc',                                   // FAMILY_HUE.armor
    feat: '#f472b6', background: '#f472b6', fighting_style: '#f472b6',
    eldritch_invocation: '#f472b6', infusion: '#f472b6', maneuver: '#f472b6',
    metamagic: '#f472b6', epic_boon: '#f472b6', pack_boon: '#f472b6', trick_shot: '#f472b6', // FAMILY_HUE.feat
    language: '#9ca3af',                                // FAMILY_HUE.language
    essence: '#c084fc',                                 // FAMILY_HUE.essence
    size_type: '#a8a29e',                               // FAMILY_HUE.size
    damage_type: '#ef4444',                             // FAMILY_HUE.damage
    environment: '#78716c', terrain_type: '#78716c',    // FAMILY_HUE.terrain
    item: '#737373', container: '#737373', instrument: '#737373', // FAMILY_HUE.object
    enchantment_template: '#eab308', enchantment: '#eab308',       // FAMILY_HUE.charge (magic items)

    // skills → FAMILY_HUE.skill
    innate_skill: '#0ea5e9', gathering_skill: '#0ea5e9', crafting_skill: '#0ea5e9',
    knowledge_skill: '#0ea5e9', weapon_skill: '#0ea5e9', armor_skill: '#0ea5e9',
    defensive_skill: '#0ea5e9', language_skill: '#0ea5e9', instrument_classification_skill: '#0ea5e9',

    // ── structural types with no rune analogue: picked to fit the rune palette, kept distinct ──
    ancestry: '#14b8a6', lineage: '#0d9488',            // teal (FAMILY_HUE.movement family)
    class: '#3b82f6', subclass: '#60a5fa', class_group: '#3b82f6', // blue (FAMILY_HUE.defense)
    creature: '#ef4444', creature_template: '#ef4444', monster_template: '#ef4444',
    creature_sub_type: '#ef4444', creature_type: '#ef4444', creature_role: '#ef4444', lair: '#ef4444',
    creature_sub_type_ancestral: '#ef4444', creature_sub_type_grouping: '#ef4444',
    creature_ancestry: '#ef4444', creature_grouping: '#ef4444', creature_lineage: '#ef4444',
};

/**
 * The creature_sub_type item_type FAMILY (2026-07-15 three-division naming) — TS twin
 * of PHP FT_CREATURE_SUB_TYPE_ITEM_TYPES / ft_is_creature_sub_type_item_type().
 * Owner's taxonomic ladder: creature_type≈Domain → creature_grouping≈Order (umbrella
 * over many ancestries; MAY carry shared traits — "all angels have this") →
 * creature_ancestry≈Genus (species package owning base movement) → creature≈Species →
 * creature_lineage≈variation (Vampires/Liches, mirrors PC Lineages). All attach via
 * the same `creature_sub_type` LAYER; only items.item_type differs — anywhere
 * item_type is compared against 'creature_sub_type', use this family check.
 * Legacy slugs retained for stale blobs.
 */
export const CREATURE_SUB_TYPE_ITEM_TYPES = [
    'creature_ancestry',
    'creature_grouping',
    'creature_lineage',
    // legacy (2026-07-14 split slugs + the original)
    'creature_sub_type',
    'creature_sub_type_ancestral',
    'creature_sub_type_grouping',
] as const;

export function isCreatureSubTypeItemType(type: unknown): boolean {
    return typeof type === 'string' && (CREATURE_SUB_TYPE_ITEM_TYPES as readonly string[]).indexOf(type) !== -1;
}

const DEFAULT_COLOR = '#6b7280'; // FAMILY_HUE.misc

// Rune-engine parity (inkFor, rune-icon-engine.ts): dark ink on bright fills, white on dark.
function inkFor(hex: string): string {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex);
    const digits = m?.[1];
    if (!digits) return '#ffffff';
    const n = parseInt(digits, 16);
    const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#0c0f17' : '#ffffff';
}

export interface ItemTypeBadgeStyle { bg: string; fg: string; }

export function itemTypeBadgeStyle(type: string): ItemTypeBadgeStyle {
    // Own-property guard: a bare `TYPE_COLOR[type]` would resolve inherited Object.prototype
    // members (constructor / toString / …) to a function instead of falling to DEFAULT_COLOR,
    // which would then be interpolated into the inline style. `type` is a fixed item_type slug,
    // but keep the "fixed palette only" guarantee airtight.
    const candidate: unknown = Object.prototype.hasOwnProperty.call(TYPE_COLOR, type) ? TYPE_COLOR[type] : undefined;
    const bg = typeof candidate === 'string' ? candidate : DEFAULT_COLOR;
    return { bg, fg: inkFor(bg) };
}

/** `background-color:…;color:…;` for a JS-generated badge's inline style attribute. */
export function itemTypeBadgeInlineStyle(type: string): string {
    const { bg, fg } = itemTypeBadgeStyle(type);
    return `background-color:${bg};color:${fg};`;
}
